import React from "react";
import {
  Button,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import Logo from "assets/academix-logo-word.svg";
import useSignUpForm from "./utils/useSignUpForm";
import {
  CheckCircleOutline,
  Email,
  KeyboardArrowRight,
  School,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import InputPassword from "../InputPassword/InputPassword";
import { Collapse } from "@mui/material";
import { Link } from "react-router-dom";
import { AUTH_ROUTE } from "app/constants/route";

type Props = {};

const SignUpForm = (props: Props) => {
  const {
    isPasswordValid,
    setIsPasswordValid,
    currentStep,
    setCurrentStep,
    isLoading,
    setIsLoading,
    formValue,
    updateFormValue,
    onSubmit,
    errMessage,
    setErrMessage,
    clearError,
    // Registration code related
    registrationCode,
    updateRegistrationCode,
    verifyUserRegistrationCode,
    codeVerification,
  } = useSignUpForm({});

  // Step 1:  registration code verification
  const renderRegistrationCodeStep = () => {
    return (
      <Stack spacing={3}>
        <Typography level="body-md" textAlign={{ xs: "center", md: "left" }}>
          🔑 Please enter your registration code
        </Typography>


        <form style={{ gap: "1rem", display: "grid" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            alignItems={"end"}
          >
            <FormControl required sx={{ width: 1 }}>
              <FormLabel>Registration Code</FormLabel>
              <Input
                type="text"
                name="registration-code"
                placeholder="Enter 6-digit code"
                value={registrationCode}
                onChange={(e) => updateRegistrationCode(e.target.value)}
                startDecorator={<School />}
                error={codeVerification.status === "failed"}
              />
            </FormControl>

            <Button
              sx={{
                height: "fit-content",
                minWidth: "120px",
                width: { xs: 1, md: "fit-content" },
              }}
              variant="outlined"
              disabled={!registrationCode || registrationCode.length !== 6}
              loading={codeVerification.loading}
              startDecorator={
                codeVerification.status === "success" && <CheckCircleOutline />
              }
              color={
                codeVerification.status === "success"
                  ? "success"
                  : codeVerification.status === "failed"
                  ? "danger"
                  : "primary"
              }
              onClick={verifyUserRegistrationCode}
            >
              {codeVerification.status === "success"
                ? "Verified"
                : codeVerification.status === "failed"
                ? "Try Again"
                : "Verify Code"}
            </Button>
          </Stack>

          {codeVerification.error && (
            <Typography color="danger" fontSize="sm">
              {codeVerification.error}
            </Typography>
          )}

          {codeVerification.status === "success" && (
            <Stack gap={2}>
              <Typography color="success" fontSize="sm">
                Registration code verified successfully!
                {codeVerification.email && (
                  <>
                    {" "}
                    Your email ({codeVerification.email}) will be pre-filled in
                    the next step.
                  </>
                )}
              </Typography>

              <Button
                endDecorator={<KeyboardArrowRight />}
                onClick={() => setCurrentStep(2)}
                color="primary"
              >
                Continue to Registration
              </Button>
            </Stack>
          )}
        </form>
      </Stack>
    );
  };

  // Step 2: User registration (username, email, password)
  const renderUserRegistrationStep = () => {
    return (
      <Stack spacing={2}>
        <Typography level="body-md" textAlign={{ xs: "center", md: "left" }}>
          🚀 Let's register your account
        </Typography>
        <form onSubmit={onSubmit} style={{ gap: "1rem", display: "grid" }}>
          {/* Username field */}
          {/* <FormControl required sx={{ width: 1 }}>
            <FormLabel>Username</FormLabel>
            <Input
              type="text"
              name="username"
              placeholder="Choose a username"
              value={formValue.username}
              onChange={(e) => updateFormValue("username", e.target.value)}
            />
          </FormControl> */}

          {/* Email field */}
          <FormControl required sx={{ width: 1 }}>
            <FormLabel>Email</FormLabel>
            <Input
              type="email"
              name="email"
              placeholder="Email"
              value={formValue.email}
              disabled={true}
              onChange={(e) => updateFormValue("email", e.target.value)}
            />
          </FormControl>

          <InputPassword
            value={formValue.password}
            onChange={(e) => updateFormValue("password", e.target.value)}
            useConfirmPassword={true}
            getErrMessage={(hasError) => {
              // console.log("SignUpForm - setting isPasswordValid:", !hasError);
              setIsPasswordValid(!hasError); // If there's no error, password is valid
            }}
          />

          {/* Display error message */}
          {errMessage && (
            <Typography
              color={errMessage.includes("successfully") ? "success" : "danger"}
              fontSize="sm"
              textAlign="center"
            >
              {errMessage}
            </Typography>
          )}

          <Stack gap={4} sx={{ mt: 2 }}>
            <Button
              type="submit"
              loading={isLoading}
              fullWidth
              size="lg"
              disabled={
                isLoading ||
                // !formValue.username ||
                !formValue.email ||
                !formValue.password ||
                !isPasswordValid
              }
              onClick={() => clearError()}
            >
              Sign Up
            </Button>
          </Stack>
        </form>
      </Stack>
    );
  };

  return (
    <Stack spacing={2} sx={{ justifyContent: "start", width: 1 }}>
      <Stack
        direction={{ md: "row" }}
        spacing={2}
        alignItems={{ xs: "center", md: "end" }}
      >
        <Typography level="h1" sx={{ lineHeight: 1 }}>
          Welcome to
        </Typography>
        <img
          src={Logo}
          style={{
            width: "190px",
            maxWidth: "90vw",
            height: "fit-content",
            objectFit: "contain",
          }}
        />
      </Stack>

      {/* Form Step Indicator */}
      <Stack
        direction="row"
        spacing={1}
        sx={{ mt: 2, mb: 1, justifyContent: { xs: "center", md: "start" } }}
      >
        <Button
          variant={currentStep === 1 ? "solid" : "outlined"}
          color={currentStep === 1 ? "primary" : "neutral"}
          size="sm"
          disabled={currentStep !== 1}
        >
          1. Registration Code
        </Button>
        <Button
          variant={currentStep === 2 ? "solid" : "outlined"}
          color={currentStep === 2 ? "primary" : "neutral"}
          size="sm"
          disabled={currentStep !== 2}
        >
          2. Account Details
        </Button>
      </Stack>

      {/* Render form based on current step */}
      {currentStep === 1
        ? renderRegistrationCodeStep()
        : renderUserRegistrationStep()}

      <Divider />
      <Stack
        mt={2}
        gap={1}
        direction="row"
        alignItems={"center"}
        justifyContent={"center"}
      >
        <Typography level="body-sm">Already have an account?</Typography>
        <Link to={AUTH_ROUTE}>
          <Typography level="body-sm" color="primary">
            Log in here
          </Typography>
        </Link>
      </Stack>
    </Stack>
  );
};

export default SignUpForm;
